import { 
    Profile as ProfileModule,
    WhatsApp as WhatsAppModule,
    WhatsAppBusiness as WhatsAppBusinessModule,
    Telegram as TelegramModule,
    Email as EmailModule,
    Users as UsersModule, // Import new Users module
    Companies as CompaniesModule, // Import new Companies module
    Security as SecurityModule, // Import new Security module
    Notifications as NotificationsModule,
    WorkerPool as WorkerPoolModule,
} from 'modules/Settings';
import {
    WhatsApp,
    Telegram,
    PersonOutline,
    EmailOutlined,
    SettingsOutlined,
    NotificationsOutlined,
    GroupOutlined, // Import GroupOutlined for Users
    BusinessOutlined, // Import BusinessOutlined for Companies
    YouTube,
    Instagram,
    MusicNote,
    WorkspacesOutlined,
} from "@mui/icons-material";
import { LockKeyhole } from 'lucide-react';
import { SideBarItems } from 'components/Sidebar';

// Lightweight Signal icon (approximated) using inline SVG so we don't add deps
const SignalIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
    >
        {/* Dashed circular outline */}
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="2 3" fill="none" />
        {/* Chat bubble tail */}
        <path d="M10.5 18.5C8 18 6 16 6 12C6 8.686 8.686 6 12 6C15.314 6 18 8.686 18 12C18 14.25 16.875 16.125 15.125 17.125" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M10.5 18.5L9 20.5L9.5 17.8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
    </svg>
);

// ModulesMenu is now a static array. Conditional logic for rendering moved to Sidebar.tsx
export const ModulesMenu: SideBarItems[] = [{
    path: "/",
    urlKey: "settings",
    text: "Settings",
    icon: <SettingsOutlined/>,
    type: "menuHeader",
    menuKey: "campaignKing",
    dictionaryKey: "sidebar-menu-settings",
    items: [
        {
            path: "/settings/",
            urlKey: "profile",
            text: "Profile",
            icon: <PersonOutline />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <ProfileModule />,
            dictionaryKey: "sidebar-menu-settings-profile",
        },
        {
            path: "/settings/",
            urlKey: "security",
            text: "Security",
            icon: <LockKeyhole />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <SecurityModule />,
            dictionaryKey: "sidebar-menu-settings-security",
        },
        {
            path: "/settings/",
            urlKey: "notifications",
            text: "Notifications",
            icon: <NotificationsOutlined />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <NotificationsModule />,
            dictionaryKey: "sidebar-menu-settings-notifications",
        },
        // These items are always included in the static menu definition
        // They will be conditionally filtered in Sidebar.tsx based on user role
        // {
        //     path: "/settings/",
        //     urlKey: "users",
        //     text: "Users",
        //     icon: <GroupOutlined />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <UsersModule />,
        //     requiredRole: "superAdmin", // Special role for super admin
        //     dictionaryKey: "sidebar-menu-settings-users",
        // },
        // {
        //     path: "/settings/",
        //     urlKey: "companies",
        //     text: "Companies",
        //     icon: <BusinessOutlined />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <CompaniesModule />,
        //     requiredRole: "superAdmin", // Special role for super admin
        //     dictionaryKey: "sidebar-menu-settings-companies",
        // },
        {
            path: "/settings/",
            urlKey: "whatsapp",
            text: "WhatsApp",
            icon: <WhatsApp />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <WhatsAppModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            dictionaryKey: "sidebar-menu-settings-whatsapp",
        },
        {
            path: "/settings/",
            urlKey: "whatsappbusiness",
            text: "WhatsApp Business",
            icon: <WhatsApp />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <WhatsAppBusinessModule />,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            requiresBusinessPlusOrEnterprise: true,
            dictionaryKey: "sidebar-menu-settings-whatsappbusiness",
        },
        {
            path: "/settings/",
            urlKey: "telegram",
            text: "Telegram",
            icon: <Telegram />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <TelegramModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            requiresBusinessPlusOrEnterprise: true,
            dictionaryKey: "sidebar-menu-settings-telegram",
        },
        // {
        //     path: "/settings/",
        //     urlKey: "sms",
        //     text: "GSM Text",
        //     icon: <SmsOutlined />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <GSMTextModule />,
        //     requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
        //     requiredRole: "admin", // Only visible to admin role, not user
        //     disabled: true,
        //     dictionaryKey: "sidebar-menu-settings-sms",
        // },
        {
            path: "/settings/",
            urlKey: "email",
            text: "Email",
            icon: <EmailOutlined />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: <EmailModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            dictionaryKey: "sidebar-menu-settings-email",
        },
        // {
        //     path: "/settings/",
        //     urlKey: "openai",
        //     text: "OpenAI",
        //     icon: <LockKeyhole />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <OpenAIModule />,
        //     requiredModule: "instantupdate.ai",
        //     requiredRole: "admin",
        //     dictionaryKey: "sidebar-menu-settings-openai",
        // },
        // {
        //     path: "/settings/",
        //     urlKey: "worker-pool",
        //     text: "Worker Pool",
        //     icon: <WorkspacesOutlined />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <WorkerPoolModule />,
        //     requiredRole: "superAdmin",
        //     allowedDomains: ['sharadtech.com','scenicmediahub.com'],
        //     dictionaryKey: "sidebar-menu-settings-worker-pool",
        // },
        // {
        //     path: "/settings/",
        //     urlKey: "gsm-worker-pool",
        //     text: "GSM Worker Pool",
        //     icon: <RouterOutlined />,
        //     type: "menuItem",
        //     menuKey: "campaignKing",
        //     module: <GSMWorkerPoolModule />,
        //     requiredRole: "superAdmin", // Only visible to super admin
        //     dictionaryKey: "sidebar-menu-settings-gsm-worker-pool",
        // },
        {
            path: "/settings/",
            urlKey: "youtube",
            text: "YouTube",
            icon: <YouTube />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: null,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            disabled: true,
            dictionaryKey: "sidebar-menu-settings-youtube",
        },
        {
            path: "/settings/",
            urlKey: "instagram",
            text: "Instagram",
            icon: <Instagram />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: null,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            disabled: true,
            dictionaryKey: "sidebar-menu-settings-instagram",
        },
        {
            path: "/settings/",
            urlKey: "tiktok",
            text: "TikTok",
            icon: <MusicNote />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: null,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            disabled: true,
            dictionaryKey: "sidebar-menu-settings-tiktok",
        },
        {
            path: "/settings/",
            urlKey: "signal",
            text: "Signal",
            icon: <SignalIcon />,
            type: "menuItem",
            menuKey: "campaignKing",
            module: null,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            disabled: true,
            dictionaryKey: "sidebar-menu-settings-signal",
        },
    ]
},


{
    path: "/",
    urlKey: "settings",
    text: "Settings",
    icon: <SettingsOutlined/>,
    type: "menuHeader",
    menuKey: "instantUpdate",
    dictionaryKey: "sidebar-menu-settings",
    items: [
        {
            path: "/settings/",
            urlKey: "profile",
            text: "Profile",
            icon: <PersonOutline />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <ProfileModule />,
            dictionaryKey: "sidebar-menu-settings-profile",
        },
        {
            path: "/settings/",
            urlKey: "security",
            text: "Security",
            icon: <LockKeyhole />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <SecurityModule />,
            dictionaryKey: "sidebar-menu-settings-security",
        },
        {
            path: "/settings/",
            urlKey: "notifications",
            text: "Notifications",
            icon: <NotificationsOutlined />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <NotificationsModule />,
            dictionaryKey: "sidebar-menu-settings-notifications",
        },
        // These items are always included in the static menu definition
        // They will be conditionally filtered in Sidebar.tsx based on user role
        // {
        //     path: "/settings/",
        //     urlKey: "users",
        //     text: "Users",
        //     icon: <GroupOutlined />,
        //     type: "menuItem",
        //     menuKey: "instantUpdate",
        //     module: <UsersModule />,
        //     requiredRole: "superAdmin", // Special role for super admin
        //     dictionaryKey: "sidebar-menu-settings-users",
        // },
        // {
        //     path: "/settings/",
        //     urlKey: "companies",
        //     text: "Companies",
        //     icon: <BusinessOutlined />,
        //     type: "menuItem",
        //     menuKey: "instantUpdate",
        //     module: <CompaniesModule />,
        //     requiredRole: "superAdmin", // Special role for super admin
        //     dictionaryKey: "sidebar-menu-settings-companies",
        // },
        {
            path: "/settings/",
            urlKey: "whatsapp",
            text: "WhatsApp",
            icon: <WhatsApp />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <WhatsAppModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            dictionaryKey: "sidebar-menu-settings-whatsapp",
        },
        {
            path: "/settings/",
            urlKey: "whatsappbusiness",
            text: "WhatsApp Business",
            icon: <WhatsApp />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <WhatsAppBusinessModule />,
            requiredModule: "instantupdate.ai",
            requiredRole: "admin",
            requiresBusinessPlusOrEnterprise: true,
            dictionaryKey: "sidebar-menu-settings-whatsappbusiness",
        },
        {
            path: "/settings/",
            urlKey: "telegram",
            text: "Telegram",
            icon: <Telegram />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <TelegramModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            requiresBusinessPlusOrEnterprise: true,
            dictionaryKey: "sidebar-menu-settings-telegram",
        },
        // {
        //     path: "/settings/",
        //     urlKey: "sms",
        //     text: "GSM Text",
        //     icon: <SmsOutlined />,
        //     type: "menuItem",
        //     menuKey: "instantUpdate",
        //     module: <GSMTextModule />,
        //     requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
        //     requiredRole: "admin", // Only visible to admin role, not user
        //     disabled: true,
        //     dictionaryKey: "sidebar-menu-settings-sms",
        // },
        {
            path: "/settings/",
            urlKey: "email",
            text: "Email",
            icon: <EmailOutlined />,
            type: "menuItem",
            menuKey: "instantUpdate",
            module: <EmailModule />,
            requiredModule: "instantupdate.ai", // Requires instantupdate.ai module access
            requiredRole: "admin", // Only visible to admin role, not user
            dictionaryKey: "sidebar-menu-settings-email",
        },
        // {
        //     path: "/settings/",
        //     urlKey: "worker-pool",
        //     text: "Worker Pool",
        //     icon: <WorkspacesOutlined />,
        //     type: "menuItem",
        //     menuKey: "instantUpdate",
        //     module: <WorkerPoolModule />,
        //     requiredRole: "superAdmin",
        //     allowedDomains: ['sharadtech.com','scenicmediahub.com'],
        //     dictionaryKey: "sidebar-menu-settings-worker-pool",
        // },
        // {
        //     path: "/settings/",
        //     urlKey: "gsm-worker-pool",
        //     text: "GSM Worker Pool",
        //     icon: <RouterOutlined />,
        //     type: "menuItem",
        //     menuKey: "instantUpdate",
        //     module: <GSMWorkerPoolModule />,
        //     requiredRole: "superAdmin", // Only visible to super admin
        //     dictionaryKey: "sidebar-menu-settings-gsm-worker-pool",
        // },
    ]
},
{
    path: "/",
    urlKey: "settings",
    text: "Settings",
    icon: <SettingsOutlined/>,
    type: "menuHeader",
    menuKey: "anamnesis",
    dictionaryKey: "sidebar-menu-settings",
    items: [
        {
            path: "/settings/",
            urlKey: "profile",
            text: "Profile",
            icon: <PersonOutline />,
            type: "menuItem",
            menuKey: "anamnesis",
            module: <ProfileModule />,
            dictionaryKey: "sidebar-menu-settings-profile",
        },
        {
            path: "/settings/",
            urlKey: "security",
            text: "Security",
            icon: <LockKeyhole />,
            type: "menuItem",
            menuKey: "anamnesis",
            module: <SecurityModule />,
            dictionaryKey: "sidebar-menu-settings-security",
        },
    ]
},
{
    path: "/",
    urlKey: "settings",
    text: "Settings",
    icon: <SettingsOutlined/>,
    type: "menuHeader",
    menuKey: "infralytiqs",
    dictionaryKey: "sidebar-menu-settings",
    items: [
        {
            path: "/settings/",
            urlKey: "profile",
            text: "Profile",
            icon: <PersonOutline />,
            type: "menuItem",
            menuKey: "infralytiqs",
            module: <ProfileModule />,
            dictionaryKey: "sidebar-menu-settings-profile",
        },
        {
            path: "/settings/",
            urlKey: "security",
            text: "Security",
            icon: <LockKeyhole />,
            type: "menuItem",
            menuKey: "infralytiqs",
            module: <SecurityModule />,
            dictionaryKey: "sidebar-menu-settings-security",
        },
    ]
}];
